import { useEffect } from "react";
import type { PurchaseEvent, PurchaseFailureCode } from "~/lib/nativeBridge";
import { usePurchaseIssueStore } from "~/stores/usePurchaseIssueStore";

/** Carinaの決済失敗系イベントのdetailに含まれる、問い合わせ添付用の決済失敗情報 */
type PurchaseIssueDetail = {
  internalTransactionId?: string;
  /** Jambo-Server決済確定APIのレスポンスコード。Apple/StoreKit側の失敗時は含まれない */
  errorCode?: number;
  /** Apple/StoreKit側のエラー種別 */
  code?: PurchaseFailureCode;
  /** 決済フローのどこまで進んで失敗したか */
  stage?: string;
  /** 失敗発生日時(ISO8601) */
  occurredAt?: string;
};

/**
 * ネイティブアプリからの課金完了/失敗通知を `purchaseEvent` カスタムイベントに変換する。
 *
 * アプリごとにイベント名が異なる:
 * - Hokage: `purchase/completed` (detail: { productId })
 * - CoachMe (Sakura) / Carina: `purchaseCompleted` (detail: {})
 * - Carina: `purchaseFailed` (detail: {}) — 決済は完了しているがJambo-Server側の
 *   確定処理(confirmPurchaseV2)に失敗した場合。ネイティブ側ではアラートを出さず、
 *   ここで受け取ってPurchaseErrorDialogを表示する
 *
 * すべてをリッスンし、アプリ内部の統一イベント `purchaseEvent` に変換する。
 * `__root.tsx` に 1 個だけマウントする。
 *
 * Carina由来の3イベント(purchaseFailed/purchaseAlreadyProcessed/purchaseStatusUnknown)は
 * 決済失敗の問い合わせ添付情報も運んでくるため、`usePurchaseIssueStore` にも保存する。
 */
export const NativePurchaseBridgeListener = () => {
  useEffect(() => {
    const { setPurchaseIssue } = usePurchaseIssueStore.getState();
    const savePurchaseIssue = (detail: PurchaseIssueDetail | undefined) => {
      setPurchaseIssue({
        internalTransactionId: detail?.internalTransactionId ?? null,
        errorCode: detail?.errorCode ?? null,
        clientFailureCode: detail?.code ?? null,
        stage: detail?.stage ?? null,
        occurredAt: detail?.occurredAt ?? null,
      });
    };

    const dispatchSucceeded = (
      productId?: string,
      alreadyProcessed?: boolean,
    ) => {
      const event: PurchaseEvent = {
        status: "succeeded",
        productId: productId ?? "",
        ...(alreadyProcessed ? { alreadyProcessed: true } : {}),
      };
      window.dispatchEvent(new CustomEvent("purchaseEvent", { detail: event }));
    };

    const dispatchFailed = (code: PurchaseFailureCode) => {
      const event: PurchaseEvent = { status: "failed", code };
      window.dispatchEvent(new CustomEvent("purchaseEvent", { detail: event }));
    };

    // Hokage: purchase/completed (detail に productId あり)
    const handleHokage = (e: Event) => {
      const detail = (e as CustomEvent<{ productId?: string }>).detail;
      dispatchSucceeded(detail?.productId);
    };

    // CoachMe (Sakura) / Carina: purchaseCompleted (detail 空)
    const handleCompleted = () => {
      dispatchSucceeded();
    };

    // Carina: purchaseFailed (detail に internalTransactionId/errorCode/code/stage/occurredAt を含む)
    const handleFailed = (e: Event) => {
      const detail = (e as CustomEvent<PurchaseIssueDetail>).detail;
      savePurchaseIssue(detail);
      dispatchFailed(detail?.code ?? "pointGrantAlreadyFailed");
    };

    const handleAlreadyProcessed = (e: Event) => {
      const detail = (e as CustomEvent<PurchaseIssueDetail>).detail;
      savePurchaseIssue(detail);
      dispatchSucceeded(undefined, true);
    };

    // Carina: purchaseStatusUnknown (detail に internalTransactionId/errorCode/code/stage/occurredAt を含む)
    // — 異常系①のリトライ上限到達
    const handleStatusUnknown = (e: Event) => {
      const detail = (e as CustomEvent<PurchaseIssueDetail>).detail;
      savePurchaseIssue(detail);
      dispatchFailed("purchaseStatusUnknown");
    };

    window.addEventListener("purchase/completed", handleHokage);
    window.addEventListener("purchaseCompleted", handleCompleted);
    window.addEventListener("purchaseFailed", handleFailed);
    window.addEventListener("purchaseAlreadyProcessed", handleAlreadyProcessed);
    window.addEventListener("purchaseStatusUnknown", handleStatusUnknown);
    return () => {
      window.removeEventListener("purchase/completed", handleHokage);
      window.removeEventListener("purchaseCompleted", handleCompleted);
      window.removeEventListener("purchaseFailed", handleFailed);
      window.removeEventListener(
        "purchaseAlreadyProcessed",
        handleAlreadyProcessed,
      );
      window.removeEventListener("purchaseStatusUnknown", handleStatusUnknown);
    };
  }, []);
  return null;
};
